import { DomApplier } from '../cssFeatures.types'
import { Css, CssObject, CssValue, SelectorObj } from '../../shared.types'
import { fromClassicCss } from '../../utils/fromClassicCss'
import { getVariantSelectors, variantsComparator } from '../../utils/variantsUtils'
import { CompVariantsViewItem } from '@wix/thunderbolt-catharsis'
import { CssContext } from '../componentCss.types'
import { getDisplayedId } from '@wix/thunderbolt-commons'
import { getInflatedRepeaterIdSelector } from '../../utils/selectorsUtils'
import { isEqual, merge, reduce } from 'lodash'

const getSelectorObj = (
	compId: string,
	targetId: string,
	idSelector: string,
	breakpointId: string,
	css: Css<CssObject | undefined>,
	designCss: Css<CssObject | undefined> | null,
	selectorToCss: Css<{ [selector: string]: CssObject } | null> | null,
	themeVariants: CompVariantsViewItem | null,
	designVariants: CompVariantsViewItem | null,
	accumulatedData: CssContext
): SelectorObj | null => {
	if (!css && !designCss && !selectorToCss) {
		return null
	}
	const generateSelectorFromCssMap = (
		cssInBreakPoint: { [variantsKey: string]: CssObject | undefined },
		variants: CompVariantsViewItem | null
	) =>
		cssInBreakPoint
			? Object.entries(cssInBreakPoint)
					.sort(variantsComparator(variants!))
					.reduce<SelectorObj>((acc, [variantKey, cssObj]) => {
						if (!cssObj) {
							return acc
						}

						const domSelector = getVariantSelectors(
							variantKey,
							variants,
							compId,
							accumulatedData,
							targetId
						).join(',')
						acc[domSelector] = cssObj

						return acc
					}, {})
			: undefined

	let cssSelectorObj: SelectorObj | undefined,
		designCssSelectorObj: SelectorObj | undefined,
		selectorToCssSelectorObj: SelectorObj | undefined

	if (css && css.css) {
		if (css.type === 'classic') {
			cssSelectorObj = fromClassicCss(css.css as CssValue, idSelector)
		} else {
			cssSelectorObj = generateSelectorFromCssMap(css.css[breakpointId], themeVariants)
		}
	}

	if (designCss) {
		if (designCss.type === 'classic') {
			designCssSelectorObj = fromClassicCss(designCss.css as CssValue, idSelector, '', true)
		} else {
			designCssSelectorObj = generateSelectorFromCssMap(designCss.css[breakpointId], designVariants)
		}
	}

	if (selectorToCss) {
		if (selectorToCss.type === 'classic') {
			selectorToCssSelectorObj = reduce(
				Object.keys(selectorToCss.css),
				(acc, currentSelector) => {
					// TODO: for some reason #masterPage is getting here and crashes the function, need to check why
					const currentCss = fromClassicCss(
						(selectorToCss.css as any)[currentSelector],
						`${idSelector} ${currentSelector}`
					)
					return merge(acc, currentCss)
				},
				{}
			)
		} else if (selectorToCss.css[breakpointId]) {
			selectorToCssSelectorObj = Object.entries(selectorToCss.css[breakpointId])
				.sort(variantsComparator(themeVariants!))
				.reduce<SelectorObj>((acc, [variantKey, cssObj]) => {
					if (!cssObj) {
						return acc
					}

					const domSelector = getVariantSelectors(
						variantKey,
						themeVariants,
						compId,
						accumulatedData,
						targetId
					).join(',')

					if (domSelector) {
						Object.keys(cssObj).forEach(
							(selector) => (acc[`${domSelector} ${selector}`] = cssObj[selector])
						)
					}

					return acc
				}, {})
		}
	}

	// This style is added to the page background component in case where an old id is used and the id isn't matched
	let pageBackgroundStyle = {}
	if (idSelector.startsWith('#pageBackground_') && cssSelectorObj) {
		const pageId = idSelector.split('#pageBackground_')[1]
		const backgroundGroupClass = `backgroundGroup__${pageId}`
		const memoUsedSelector = `.${backgroundGroupClass} :not(${idSelector})`
		pageBackgroundStyle = { [memoUsedSelector]: cssSelectorObj[idSelector] }
	}
	return merge({}, designCssSelectorObj, cssSelectorObj, selectorToCssSelectorObj, pageBackgroundStyle)
}

export const compCssMappersDomApplier: DomApplier<'compCssMappers'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ compCssMappers, themeVariants, compDesignCssMappers, compSelectorToCssMappers, isStylable, designVariants },
	accumulatedData
) => {
	if (!compCssMappers && !compDesignCssMappers && !compSelectorToCssMappers) {
		return null
	}

	if (isStylable) {
		accumulatedData = {
			...accumulatedData,
			isStylable: true,
		}
	}

	const { '': templateCss, ...repeatedItemsCss } = compCssMappers || {}
	// TODO - When adding support for Repeated items do this -
	// const { '': templateDesignCss, ...repeatedItemsDesignCss } = compDesignCssMappers || {}
	let templateCssResult = getSelectorObj(
		compId,
		compId,
		idSelector,
		breakpointId,
		templateCss,
		compDesignCssMappers || null,
		compSelectorToCssMappers || null,
		themeVariants,
		designVariants,
		accumulatedData
	)!

	if (templateCss?.additionalCss) {
		const { isInRepeater } = accumulatedData
		const additionalCss = reduce(
			templateCss.additionalCss,
			(acc, { css, repeated }, selector) => ({
				...acc,
				...getSelectorObj(
					selector,
					selector,
					`#${selector}`,
					breakpointId,
					css,
					null,
					null,
					themeVariants,
					designVariants,
					{
						...accumulatedData,
						isInRepeater: repeated && isInRepeater,
					}
				),
			}),
			{} as Record<string, any>
		)

		templateCssResult = {
			...templateCssResult,
			...additionalCss,
		}
	}

	if (!Object.keys(repeatedItemsCss).length) {
		return templateCssResult
	}

	return Object.entries(repeatedItemsCss).reduce<SelectorObj>((acc, [context, compCss]) => {
		if (isEqual(compCss, templateCss)) {
			return acc
		}

		const displayedId = getDisplayedId(compId, context)
		const contextCssResult = getSelectorObj(
			compId,
			displayedId,
			getInflatedRepeaterIdSelector(displayedId),
			breakpointId,
			compCss,
			null,
			null,
			themeVariants,
			designVariants,
			accumulatedData
		)

		return contextCssResult ? Object.assign(acc, contextCssResult) : acc
	}, templateCssResult)
}
